import { useEffect, useState, useCallback } from 'react';

export type CameraPermissionState = 'unknown' | 'prompt' | 'granted' | 'denied' | 'unavailable';

interface UseBarcodePermissionResult {
  status: CameraPermissionState;
  request: () => Promise<boolean>;
}

/**
 * Hook that reports the current camera-permission state and exposes a request() helper.
 *
 * Browsers differ in how permissions are queryable — we guard every call and fall back
 * gracefully to 'prompt' when the Permissions API is unavailable.
 */
export function useBarcodePermission(): UseBarcodePermissionResult {
  const [status, setStatus] = useState<CameraPermissionState>('unknown');

  useEffect(() => {
    let cancelled = false;

    async function check() {
      if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
        if (!cancelled) setStatus('unavailable');
        return;
      }

      if (!navigator.permissions?.query) {
        if (!cancelled) setStatus('prompt');
        return;
      }

      try {
        const result = await navigator.permissions.query({ name: 'camera' as PermissionName });
        if (cancelled) return;
        setStatus(result.state as CameraPermissionState);
        result.onchange = () => {
          setStatus(result.state as CameraPermissionState);
        };
      } catch {
        if (!cancelled) setStatus('prompt');
      }
    }

    check();
    return () => {
      cancelled = true;
    };
  }, []);

  const request = useCallback(async (): Promise<boolean> => {
    if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
      setStatus('unavailable');
      return false;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
      stream.getTracks().forEach((t) => t.stop());
      setStatus('granted');
      return true;
    } catch (err) {
      console.warn('[useBarcodePermission] getUserMedia denied:', err);
      setStatus('denied');
      return false;
    }
  }, []);

  return { status, request };
}
